/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Stack,
  Group,
  Text,
  Badge,
  LoadingOverlay,
  Checkbox,
  Button,
  Modal,
  Select,
  TextInput
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconClipboardCheck, IconFilter } from '@tabler/icons-react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { pathnames } from 'routes/yearlyCalendar';

import PageTitle from 'components/PageTitle';
import Toolbar from 'components/Toolbar';
import Card from 'components/Card';
import DataTables from 'components/DataTables';
import { DataTableColumn } from 'components/DataTables';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
import Dates from 'components/Dates';
import BulkApprovalModal from './components/BulkApprovalModal';

import {
  yearlyCalendarSchedulePlanService,
  YearlyCalendarSchedulePlan,
  YearlyCalendarFilters
} from 'services/yearlyCalendar/schedulePlanService';

const SchedulePlanPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const userRole = localStorage.getItem('userRole') || 'manager';
  const isManager = userRole === 'manager';

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const searchParams = new URLSearchParams(location.search);
  const defaultSearch = searchParams.get('search') || '';

  const [previousSearch, setPreviousSearch] = useState(defaultSearch);

  // Modal state
  const [openedDeleteModal, setOpenedDeleteModal] = useState<boolean>(false);
  const [openedBulkApprovalModal, setOpenedBulkApprovalModal] = useState<boolean>(false);
  const [openedFilterModal, setOpenedFilterModal] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<YearlyCalendarSchedulePlan | null>(null);

  // Checkbox selection state
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);

  // Filter state with activated filters
  const [filters, setFilters] = useState<YearlyCalendarFilters>({
    search: defaultSearch,
    startDate: `${new Date().getFullYear() - 1}-01-01`,
    endDate: `${new Date().getFullYear() + 1}-12-31`,
    statusApproval: '',
    sortBy: 'planningName',
    sortOrder: 'desc',
    programType: ''
  });

  const [tempFilters, setTempFilters] = useState<YearlyCalendarFilters>(filters);

  const {
    data: schedulePlansData,
    isLoading,
    isFetching,
    error
  } = useQuery({
    queryKey: ['yearlyCalendarSchedulePlans', defaultSearch, currentPage, pageSize, filters],
    queryFn: () =>
      yearlyCalendarSchedulePlanService.getSchedulePlanList(currentPage, pageSize, {
        ...filters,
        search: defaultSearch
      }),
    refetchOnWindowFocus: false,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404 || error?.message?.includes('No schedule plans found')) {
        return false;
      }
      return failureCount < 3;
    }
  });

  // Check if error is due to no search results (404 or empty data)
  const isNoDataError =
    error &&
    ((error as any)?.response?.status === 404 ||
      (error as any)?.message?.includes('No schedule plans found'));

  // If it's a no-data error, treat it as successful with empty data
  const displayData = isNoDataError
    ? {
        data: [],
        pageNumber: 1,
        pageSize: 10,
        totalPages: 0,
        totalRecords: 0
      }
    : schedulePlansData;

  const approvablePlans = useMemo(() => {
    return (displayData?.data || []).filter(
      (plan) => plan.statusApproval.toLocaleUpperCase() === 'PLANNED'
    );
  }, [displayData?.data]);

  const handleSelectPlan = (planId: string, checked: boolean) => {
    setSelectedPlans((prev) => {
      if (checked) {
        return [...prev, planId];
      } else {
        return prev.filter((id) => id !== planId);
      }
    });
  };

  // Handle select all checkbox
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const approvablePlanIds = approvablePlans.map((plan) => plan.msLCMSPlanningID);
      setSelectedPlans(approvablePlanIds);
    } else {
      setSelectedPlans([]);
    }
  };

  // Check if all approvable plans are selected
  const isAllSelected =
    approvablePlans.length > 0 &&
    selectedPlans.length === approvablePlans.length &&
    approvablePlans.every((plan) => selectedPlans.includes(plan.msLCMSPlanningID));

  // Check if some plans are selected
  const isIndeterminate = selectedPlans.length > 0 && selectedPlans.length < approvablePlans.length;

  // Get selected plan objects for bulk approval
  const selectedPlanObjects = useMemo(() => {
    return approvablePlans.filter((plan) => selectedPlans.includes(plan.msLCMSPlanningID));
  }, [approvablePlans, selectedPlans]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => yearlyCalendarSchedulePlanService.deleteSchedulePlan(id),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Schedule plan deleted successfully',
        icon: <IconCheck />,
        color: 'green'
      });
      queryClient.invalidateQueries({ queryKey: ['yearlyCalendarSchedulePlans'] });
      setOpenedDeleteModal(false);
      setSelectedPlan(null);
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete schedule plan',
        color: 'red'
      });
    }
  });

  // Event handlers
  const handleView = (plan: YearlyCalendarSchedulePlan) => {
    navigate(pathnames.schedulePlanDetail.replace(':id', plan.msLCMSPlanningID));
  };

  const handleEdit = (plan: YearlyCalendarSchedulePlan) => {
    navigate(`${pathnames.schedulePlan}/edit/${plan.msLCMSPlanningID}`);
  };

  const handleDelete = (plan: YearlyCalendarSchedulePlan) => {
    setSelectedPlan(plan);
    setOpenedDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedPlan) return;
    await deleteMutation.mutateAsync(selectedPlan.msLCMSPlanningID);
  };

  // Filter handlers
  const handleOpenFilter = () => {
    setTempFilters(filters);
    setOpenedFilterModal(true);
  };

  const handleApplyFilter = () => {
    setFilters(tempFilters);
    setCurrentPage(1);
    setOpenedFilterModal(false);
  };

  const handleResetFilter = () => {
    const resetFilters: YearlyCalendarFilters = {
      search: defaultSearch,
      startDate: `${new Date().getFullYear() - 1}-01-01`,
      endDate: `${new Date().getFullYear() + 1}-12-31`,
      statusApproval: 'approved',
      sortBy: 'planningName',
      sortOrder: 'asc'
    };
    setTempFilters(resetFilters);
    setFilters(resetFilters);
    setCurrentPage(1);
    setOpenedFilterModal(false);
  };

  // Bulk approval handlers
  const handleBulkApproval = () => {
    if (selectedPlans.length === 0) {
      notifications.show({
        title: 'No Selection',
        message: 'Please select at least one plan to approve',
        color: 'yellow'
      });
      return;
    }
    setOpenedBulkApprovalModal(true);
  };

  const handleBulkApprovalClose = () => {
    // close modal and reset selected plans in the list
    setOpenedBulkApprovalModal(false);
    setSelectedPlans([]);
  };

  const handleBulkApprovalSuccess = () => {
    setSelectedPlans([]);
    setOpenedBulkApprovalModal(false);
    queryClient.invalidateQueries({ queryKey: ['yearlyCalendarSchedulePlans'] });
  };

  // Clear selections when data changes
  useEffect(() => {
    setSelectedPlans([]);
  }, [displayData?.data]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Handle search changes
  useEffect(() => {
    if (defaultSearch !== previousSearch) {
      setCurrentPage(1);
      setPreviousSearch(defaultSearch);
    }
  }, [defaultSearch, previousSearch]);

  // Sync with API response
  useEffect(() => {
    if (displayData?.pageNumber && displayData.pageNumber !== currentPage) {
      setCurrentPage(displayData.pageNumber);
    }
  }, [displayData?.pageNumber]);

  useEffect(() => {
    if (displayData?.pageSize && displayData.pageSize !== pageSize) {
      setPageSize(displayData.pageSize);
    }
  }, [displayData?.pageSize]);

  const columns: DataTableColumn[] = [
    ...(isManager
      ? [
          {
            heading:
              filters.statusApproval?.toLowerCase() === 'planned' ? (
                <Checkbox
                  title='Select all planned in this page'
                  aria-label='Select all planned in this page'
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={(e) => handleSelectAll(e.currentTarget.checked)}
                />
              ) : (
                'Select'
              ),
            props: (row: YearlyCalendarSchedulePlan) => {
              if (row.statusApproval.toLocaleUpperCase() !== 'PLANNED') {
                return <div style={{ width: 20 }} />;
              }

              return (
                <Checkbox
                  checked={selectedPlans.includes(row.msLCMSPlanningID)}
                  onChange={(event) =>
                    handleSelectPlan(row.msLCMSPlanningID, event.currentTarget.checked)
                  }
                />
              );
            },
            sortable: false,
            active: true,
            id: 'checkbox',
            width: 50
          }
        ]
      : []),
    {
      heading: 'Planning Name',
      props: (row: YearlyCalendarSchedulePlan) => (
        <div>
          <Text fw={500} size='sm' lineClamp={1}>
            {row.planningName}
          </Text>
          <Text size='xs' c='dimmed' lineClamp={2}>
            {row.planningDescription}
          </Text>
        </div>
      ),
      sortable: true,
      active: true,
      id: 'planningName'
    },
    {
      heading: 'Program Type',
      props: (row: YearlyCalendarSchedulePlan) => (
        <Badge variant='light' color={row.programType === 'modular' ? 'blue' : 'green'} size='sm'>
          {row.programType}
        </Badge>
      ),
      sortable: true,
      active: true,
      id: 'programType'
    },
    {
      heading: 'Status',
      props: (row: YearlyCalendarSchedulePlan) => {
        const getStatusColor = (status: string) => {
          switch (status?.toLowerCase()) {
            case 'planned':
              return 'yellow';
            case 'approved':
              return 'green';
            case 'rejected':
              return 'red';
            default:
              return 'gray';
          }
        };

        return (
          <Badge
            variant='light'
            color={getStatusColor(row.statusApproval)}
            size='sm'
            style={{
              whiteSpace: 'nowrap',
              overflow: 'visible',
              display: 'inline-block',
              width: '100%',
              textAlign: 'center'
            }}
          >
            {row.statusApproval.toUpperCase() === 'PLANNED'
              ? 'UNPLANNED'
              : row.statusApproval || 'Unknown'}
          </Badge>
        );
      },
      sortable: true,
      active: true,
      id: 'statusApproval',
      width: 120
    },
    {
      heading: 'Schedule Period',
      props: (row: YearlyCalendarSchedulePlan) => (
        <div>
          <Text size='sm'>
            <Dates date={row.planningStart} fz={12} />
          </Text>
          <Text size='xs' c='dimmed'>
            to <Dates date={row.planningEnd} fz={12} />
          </Text>
        </div>
      ),
      sortable: true,
      active: true,
      id: 'planningStart'
    },
    {
      heading: 'Summary',
      props: (row: YearlyCalendarSchedulePlan) => (
        <Group gap='xs'>
          <Badge variant='outline' size='xs'>
            {row.totalCourses || 0} courses
          </Badge>
          <Badge variant='outline' size='xs'>
            {row.totalPositions || 0} positions
          </Badge>
          {'totalBudget' in row && typeof (row as any).totalBudget === 'number' && (
            <Badge variant='outline' size='xs' color='violet'>
              {new Intl.NumberFormat('id-ID', {
                style: 'currency',
                currency: 'IDR',
                maximumFractionDigits: 0
              }).format((row as any).totalBudget as number)}
            </Badge>
          )}
        </Group>
      ),
      active: true,
      id: 'summary'
    },
    {
      heading: 'Created',
      props: (row: YearlyCalendarSchedulePlan) => (
        <div>
          <Text size='sm'>
            <Dates date={row.dateIn} fz={12} />
          </Text>
          <Text size='xs' c='dimmed'>
            by {row.userIn}
          </Text>
        </div>
      ),
      sortable: true,
      active: true,
      id: 'dateIn'
    },
    {
      heading: 'Last Modified',
      props: (row: YearlyCalendarSchedulePlan) => (
        <div>
          {row.dateUp ? (
            <>
              <Text size='sm'>
                <Dates date={row.dateUp} fz={12} />
              </Text>
              <Text size='xs' c='dimmed'>
                by {row.userUp}
              </Text>
            </>
          ) : (
            <Text size='xs' c='dimmed'>
              Not modified
            </Text>
          )}
        </div>
      ),
      sortable: true,
      active: true,
      id: 'dateUp'
    },
    {
      heading: 'Action',
      active: true,
      props: 'action',
      id: 'action'
    }
  ];

  // Check if error is due to no search results (404 or empty data)
  // Prepare table data
  const tableData = {
    data: displayData?.data || [],
    pageNumber: displayData?.pageNumber || 1,
    pageSize: displayData?.pageSize || 10,
    totalPages: displayData?.totalPages || 0,
    totalRecords: displayData?.totalRecords || 0
  };

  if (error && !isNoDataError) {
    return (
      <Stack gap='md'>
        <PageTitle
          title='Schedule Plan'
          description='Manage your learning timelines and educational calendars in one place.'
        />
        <Card>
          <Text c='red'>Failed to load schedule plans. Please try again later.</Text>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack gap='md'>
      <PageTitle
        title='Schedule Plan'
        description='Manage your learning timelines and educational calendars in one place. Create new schedules, make changes to existing plans, remove outdated schedules, or review current learning activities and assessments.'
      />

      <Card>
        <Stack>
          {/* Delete Confirmation Modal */}
          <ModalPopupConfirmationComponent
            buttonText='Delete'
            opened={openedDeleteModal}
            statusProcess='delete'
            open={() => setOpenedDeleteModal(true)}
            close={() => setOpenedDeleteModal(false)}
            onSubmit={confirmDelete}
          />

          {isManager && (
            <BulkApprovalModal
              opened={openedBulkApprovalModal}
              onClose={handleBulkApprovalClose}
              selectedPlans={selectedPlanObjects}
              onSuccess={handleBulkApprovalSuccess}
            />
          )}

          {/* Filter Modal */}
          <Modal
            opened={openedFilterModal}
            onClose={() => setOpenedFilterModal(false)}
            title='Filter Schedule Plans'
            size='md'
          >
            <Stack gap='md'>
              <DateInput
                label='Start Date'
                placeholder='Select start date'
                value={tempFilters.startDate ? new Date(tempFilters.startDate) : null}
                onChange={(value) =>
                  setTempFilters((prev) => ({
                    ...prev,
                    startDate: value?.toISOString().split('T')[0] || ''
                  }))
                }
              />

              <DateInput
                label='End Date'
                placeholder='Select end date'
                value={tempFilters.endDate ? new Date(tempFilters.endDate) : null}
                onChange={(value) =>
                  setTempFilters((prev) => ({
                    ...prev,
                    endDate: value?.toISOString().split('T')[0] || ''
                  }))
                }
              />

              <Select
                label='Status Approval'
                placeholder='Select status'
                value={tempFilters.statusApproval}
                onChange={(value) =>
                  setTempFilters((prev) => ({
                    ...prev,
                    statusApproval: value || ''
                  }))
                }
                data={[
                  { value: '', label: 'All Status' },
                  { value: 'planned', label: 'Unplanned' },
                  { value: 'approved', label: 'Approved' },
                  { value: 'rejected', label: 'Rejected' }
                ]}
              />

              <Select
                label='Program Type'
                placeholder='type of the program'
                value={tempFilters.programType}
                onChange={(value) =>
                  setTempFilters((prev) => ({
                    ...prev,
                    programType: value || ''
                  }))
                }
                data={[
                  { value: '', label: 'All Status' },
                  { value: 'modular', label: 'Modular' },
                  { value: 'program', label: 'Program' },
                  { value: 'Emerging Needs', label: 'Emerging Needs' }
                ]}
              />

              <Group justify='flex-end' mt='md'>
                <Button variant='outline' onClick={() => setOpenedFilterModal(false)}>
                  Cancel
                </Button>

                <Button onClick={handleApplyFilter}>Apply Filter</Button>
              </Group>
            </Stack>
          </Modal>

          {/* Toolbar with Bulk Actions */}
          <Group justify='space-between'>
            <Toolbar
              showSearch={true}
              showFilter={true}
              showCreateButton={false}
              searchPlaceholder='Search schedule plans...'
              defaultSearchValue={defaultSearch}
              onFilter={handleOpenFilter}
            />

            {/* Bulk Action Button for Managers */}
            {isManager && selectedPlans.length > 0 && (
              <Button
                leftSection={<IconClipboardCheck size={16} />}
                onClick={handleBulkApproval}
                variant='filled'
                color='blue'
              >
                Bulk Approve ({selectedPlans.length})
              </Button>
            )}
          </Group>

          {/* Selection Summary for Managers */}
          {isManager && approvablePlans.length > 0 && (
            <Group justify='space-between' align='center'>
              <Text size='sm' c='dimmed'>
                {selectedPlans.length} of {approvablePlans.length} approvable plans selected
              </Text>

              {filters.statusApproval?.toLowerCase() === 'planned' && (
                <Checkbox
                  label='Select all planned in this page'
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={(e) => handleSelectAll(e.currentTarget.checked)}
                />
              )}
            </Group>
          )}

          <div style={{ position: 'relative', overflowX: 'auto' }}>
            <LoadingOverlay visible={isLoading || isFetching || deleteMutation.isPending} />

            {/* Inner wrapper forces table to take its natural width and allows horizontal scrolling */}
            <div style={{ minWidth: 1200 }}>
              <DataTables
                columns={columns}
                data={tableData.data}
                loading={isLoading}
                isNumbering={true}
                activeSize={pageSize}
                sizing={[
                  { label: '10', value: 10 },
                  { label: '20', value: 20 },
                  { label: '50', value: 50 },
                  { label: '100', value: 100 }
                ]}
                withPagination={true}
                totalData={tableData.totalRecords}
                currentPage={currentPage}
                currentSize={pageSize}
                handleChangePage={handlePageChange}
                handleChangeSize={handleSizeChange}
                handleView={handleView}
                handleEdit={handleEdit}
                // handleDelete={handleDelete}
                viewText='View'
                editText='Edit'
                deleteText='Delete'
                emptyMessageTitle='No schedule plans available.'
                emptyMessageDescription='No schedule plans found matching your criteria.'
              />
            </div>
          </div>
        </Stack>
      </Card>
    </Stack>
  );
};

export default SchedulePlanPage;
